'use client';

import Hero from '@/components/Hero/Hero';
import TableOfContents from '@/components/TableOfContents/TableOfContents';
import IntroCard from '@/components/IntroCard/IntroCard';
import BrandCard from '@/components/BrandCard/BrandCard';
import VideoCard from '@/components/VideoCard/VideoCard';
import WistiaVideoBase from '@/components/VideoCard/WistiaVideo/WistiaVideoBase';
import ImageGrid from '@/components/ImageGrid/ImageGrid';
import InformationCard from '@/components/InformationCard/InformationCard';
import FeatureCard from '@/components/FeatureCard/FeatureCard';
import FeatureCardGrid from '@/components/FeatureCardGrid/FeatureCardGrid';
import FullWidthFeatureCard from '@/components/FullWidthFeatureCard/FullWidthFeatureCard';
import CtaCard from '@/components/CtaCard/CtaCard';
import ContentGallery from '@/components/ContentGallery/ContentGallery';
import PhotoGallery from '@/components/PhotoGallery/PhotoGallery';
import PhotoGalleryGrid from '@/components/PhotoGalleryGrid/PhotoGalleryGrid';
import {
  isTableOfContentsEnabled,
  isWistiaVideoIncluded,
  isHeroTextHidden,
  isVideoCardWidescreen,
  isHeroTheaterMode,
} from '@/utils/landingPageFeatures';

/* eslint-disable no-unused-vars */
const ClientPage = ({ landingPage, featureFlags }) => {
  // console.log(featureFlags); // Leaving this here as a check for Feature Flags

  type TAlignmentHistory = Record<string, 'left' | 'right'> & {
    previousSection?: 'left' | 'right';
  };

  // Sets the alignment of cards on a page
  const alignmentHistory: TAlignmentHistory = {};
  const discoverAlignmentHistory = ({
    key,
    pref = 'auto',
    limitScope = true,
  }: {
    key: string;
    pref: 'auto' | 'left' | 'right';
    limitScope?: boolean;
  }): 'left' | 'right' => {
    if (pref !== 'auto') return (alignmentHistory[key] = pref);

    if (!limitScope)
      return (alignmentHistory.previousSection =
        alignmentHistory.previousSection === 'left' ? 'right' : 'left');

    return (alignmentHistory[key] =
      alignmentHistory[key] === 'left' ? 'right' : 'left');
  };

  return (
    <>
      {landingPage.hero && (
        <Hero
          title={landingPage.hero?.title}
          description={landingPage.hero?.description}
          isHeroTextHidden={isHeroTextHidden({
            features: landingPage.landingPageFeatures,
          })}
          isHeroTheaterMode={isHeroTheaterMode({
            features: landingPage.landingPageFeatures,
          })}
          video={landingPage.hero?.video}
          image={landingPage.hero?.image}
          buttonLabel={landingPage?.hero?.button?.label}
          buttonUrl={landingPage?.hero?.button?.url}
          backgroundColor={landingPage?.hero?.backgroundColor}
          heroWaveOutlineHeight={landingPage.heroWaveOutlineHeight}
        />
      )}
      {/* TABLE OF CONTENTS */}
      {landingPage.anchorTags &&
        isTableOfContentsEnabled({
          features: landingPage.landingPageFeatures,
        }) && <TableOfContents anchorTags={landingPage.anchorTags} />}
      {/* SECTIONS */}
      {landingPage?.sections?.map((section, key) => {
        switch (section.__typename) {
          case 'IntroCard':
            return (
              <IntroCard
                key={key}
                title={section.title}
                titleSizing={section.titleSizing}
                disclaimer={section.disclaimer}
                paddingTop={section.padding?.top}
                paddingBottom={section.padding?.bottom}
                description={section.description}
                includeSegment={section.includeSegment}
                paragraphStyle={section.paragraphStyle}
                textColorTw={section.textColorTw}
                backgroundColorTw={section.backgroundColorTw}
              />
            );

          case 'BrandCard':
            return (
              <BrandCard
                key={key}
                brand={section.brand}
                description={section.description}
                disclaimer={section.disclaimer}
                image={section.image}
                isClaytonBuiltExclusive={section.isClaytonBuiltExclusive}
                buttonLabel={section.button?.label}
                buttonUrl={section.button?.url}
                buttonStyle={section.button?.style}
                buttonOpenNewWindow={section.button?.isOpenNewWindow}
                alignment={discoverAlignmentHistory({
                  key: 'brandCard',
                  pref: section.alignment,
                })}
                anchorTagRef={section.anchorTag?.ref}
                includeCardShadow={section.includeCardShadow}
                includeSegment={section.includeSegment}
                backgroundColorTw={section.backgroundColorTw}
              />
            );

          case 'VideoCard':
            return (
              <VideoCard
                key={key}
                title={section.title}
                description={section.description}
                video={section.video}
                buttonLabel={section.button?.label}
                buttonUrl={section.button?.url}
                buttonStyle={section.button?.style}
                buttonOpenNewWindow={section.button?.isOpenNewWindow}
                alignment={discoverAlignmentHistory({
                  key: 'videoCard',
                  pref: section.alignment,
                })}
                anchorTagRef={section.anchorTag?.ref}
                isWidescreen={isVideoCardWidescreen({
                  features: landingPage.landingPageFeatures,
                })}
                includeCardShadow={section.includeCardShadow}
                includeSegment={section.includeSegment}
                backgroundColorTw={section.backgroundColorTw}
              />
            );

          case 'FeatureCard':
            return section.isFullWidth === true ? (
              <FullWidthFeatureCard
                key={key}
                title={section.title}
                description={section.description}
                image={section.image}
                buttonUrl={section.button?.url}
                buttonOpenNewWindow={section.button?.isOpenNewWindow}
                includeSegment={section.includeSegment}
                anchorTagRef={section.anchorTag?.ref}
              />
            ) : (
              <FeatureCard
                key={key}
                title={section.title}
                description={section.description}
                image={section.image}
                buttonLabel={section.button?.label}
                buttonUrl={section.button?.url}
                buttonStyle={section.button?.style}
                buttonOpenNewWindow={section.button?.isOpenNewWindow}
                alignment={discoverAlignmentHistory({
                  key: 'featureCard',
                  pref: section.alignment,
                  limitScope: false,
                })}
                anchorTagRef={section.anchorTag?.ref}
                includeCardShadow={section.includeCardShadow}
                includeSegment={section.includeSegment}
                backgroundColor={section.backgroundColor}
                backgroundColorTw={section.backgroundColorTw}
              />
            );

          case 'FeatureCardGrid':
            return (
              <FeatureCardGrid
                key={key}
                featureCards={section.featureCards}
                isImagesOnly={section.isImagesOnly}
                includeCardShadow={section.includeCardShadow}
                backgroundColor={section.backgroundColor}
              />
            );

          case 'LandingPageCard':
            return section.isFullWidth === true ? (
              <FullWidthFeatureCard
                key={key}
                title={section.title}
                description={section.description}
                image={section.image}
                buttonUrl={section.button?.url}
                buttonOpenNewWindow={section.button?.isOpenNewWindow}
                brand={section.brand}
                anchorTagRef={section.anchorTag?.ref}
                includeSegment={section.includeSegment}
              />
            ) : (
              <FeatureCard
                key={key}
                title={section.title}
                description={section.description}
                image={section.image}
                buttonLabel={section.button?.label}
                buttonUrl={section.button?.url}
                buttonStyle={section.button?.style}
                buttonOpenNewWindow={section.button?.isOpenNewWindow}
                brand={section.brand}
                alignment={discoverAlignmentHistory({
                  key: 'landingPageCard',
                  pref: section.alignment,
                  limitScope: false,
                })}
                anchorTagRef={section.anchorTag?.ref}
                includeCardShadow={section.includeCardShadow}
                includeSegment={section.includeSegment}
                backgroundColor={section.backgroundColor}
                backgroundColorTw={section.backgroundColorTw}
              />
            );

          case 'StudioImageGrid':
            return (
              <ImageGrid
                key={key}
                title={section.title}
                content={section.content}
                anchorTagRef={section.anchorTag?.ref}
              />
            );

          case 'InformationCard':
            return (
              <InformationCard
                key={key}
                json={section.json}
                links={section.links}
                linkStyle={section.linkStyle}
                paragraphStyle={section.paragraphStyle}
                anchorTagRef={section.anchorTag?.ref}
                includeCardShadow={section.includeCardShadow}
                includeSegment={section.includeSegment}
                backgroundColorTw={section.backgroundColorTw}
                paddingTop={section.padding?.top}
                paddingBottom={section.padding?.bottom}
              />
            );

          case 'CtaCard':
            return <CtaCard key={key} section={section} />;

          case 'ContentGallery': {
            return (
              <ContentGallery
                key={key}
                title={section.title}
                galleryContent={section.galleryContent}
                includeCardTitles={section.includeCardTitles}
              />
            );
          }

          case 'PhotoGallery': {
            return (
              <PhotoGallery
                key={key}
                title={section.title}
                description={section.description}
                imagesCollection={section.imagesCollection}
                isDigitalModel={section.isDigitalModel}
                matterportId={section.matterportId}
                modelTitle={section.modelTitle}
                numberBeds={section.numberBeds}
                numberBaths={section.numberBaths}
                modelLength={section.modelLength}
                modelWidth={section.modelWidth}
              />
            );
          }

          case 'PhotoGalleryGrid': {
            return (
              <PhotoGalleryGrid
                key={key}
                title={section.title}
                description={section.description}
                photoGalleries={section.photoGalleries}
                includeCardShadow={section.includeCardShadow}
                backgroundColor={section.backgroundColor}
              />
            );
          }

          default:
            return null;
        }
      })}
      {/* Include the shared video script, when there's at least one video. */}
      {isWistiaVideoIncluded({
        features: landingPage.landingPageFeatures,
      }) && <WistiaVideoBase />}
    </>
  );
};

export default ClientPage;
